// Recordings table: analysed clips merged with the uploaded files.
// PROCESSING rows are re-read every 2 s until inference settles.
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  Chip,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  BarChart as SpectrogramIcon,
  Replay as RetryIcon,
  InfoOutlined as InfoIcon,
  Warning as WarningIcon,
  Description as FileIcon,
  ChevronRight as ArrowRightIcon,
} from '@mui/icons-material';
import clientService from 'src/clientService';
import { resolveAudioUrl } from '../resolveAudioUrl';
import AudioAnalyzerPopup from './AudioAnalyzerPopup';
import MarkCompleteButton from './MarkCompleteButton';

interface AcousticFile {
  fileName: string;
  fileId?: string;
  timestamp: string;
  duration: string;
  status: 'ANALYZED' | 'PROCESSING' | 'FAILED';
  clipData?: any;
  detections?: Array<{
    name: string;
    scientificName: string;
    confidence: number;
  }>;
}

interface AcousticDataViewProps {
  onUploadClick?: () => void;
  deploymentId?: string;
  siteName?: string;
  deploymentName?: string;
  deploymentsList?: any[];
  onDeploymentSelect?: (id: string, name: string, siteName: string) => void;
  initialSpeciesFilter?: string;
  refreshKey?: number;
}

const nameKey = (value?: string | null) => {
  const base = (value || '').split(/[\\/]/).pop() || '';
  return base.replace(/\.[^.]+$/, '').toLowerCase();
};

const STATUS_BY_FILE_STATUS: Record<string, AcousticFile['status']> = {
  COMPLETED: 'ANALYZED',
  FAILED: 'FAILED',
  PENDING: 'PROCESSING',
  RUNNING: 'PROCESSING',
};

const formatTimestamp = (iso?: string) => {
  if (!iso) return 'N/A';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const AudioRowPlayer: React.FC<{ file: AcousticFile }> = ({ file }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Rows are re-read every couple of seconds while inference runs, which replaces every
  // row object; only a different recording should stop the one that is playing.
  const rowKey = file.fileId || file.fileName;
  const activeRow = useRef(rowKey);
  const mounted = useRef(false);

  useEffect(() => {
    activeRow.current = rowKey;
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsPlaying(false);
    setUnavailable(false);
  }, [rowKey]);

  const togglePlay = async () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }

    try {
      if (!audioRef.current) {
        const audioUrl = await resolveAudioUrl(file);
        if (activeRow.current !== rowKey) return;
        if (!audioUrl) {
          setUnavailable(true);
          return;
        }
        const audio = new Audio(audioUrl);
        audio.onended = () => setIsPlaying(false);
        audio.onerror = () => {
          setIsPlaying(false);
          setUnavailable(true);
        };
        audioRef.current = audio;
      }
      await audioRef.current.play();
      setIsPlaying(true);
    } catch {
      if (activeRow.current === rowKey) setUnavailable(true);
      setIsPlaying(false);
    }
  };

  return (
    <Box
      onClick={() => togglePlay()}
      title={unavailable ? 'Audio unavailable' : isPlaying ? 'Pause' : 'Play'}
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px',
        borderRadius: '50%', border: '1px solid rgba(255, 255, 255, 0.1)', cursor: 'pointer',
        color: unavailable ? '#F59E0B' : isPlaying ? '#10B981' : 'rgba(255, 255, 255, 0.5)',
        '&:hover': { color: '#fff', borderColor: 'rgba(255,255,255,0.3)', bgcolor: isPlaying ? 'rgba(16,185,129,0.1)' : 'rgba(255,255,255,0.05)' }
      }}
    >
      {isPlaying ? <PauseIcon sx={{ fontSize: 16 }} /> : <PlayIcon sx={{ fontSize: 16 }} />}
    </Box>
  );
};

const AcousticDataView: React.FC<AcousticDataViewProps> = ({
  onUploadClick,
  deploymentId,
  siteName,
  deploymentName,
  deploymentsList,
  onDeploymentSelect,
  initialSpeciesFilter = '',
  refreshKey = 0,
}) => {
  const [speciesFilter, setSpeciesFilter] = useState(initialSpeciesFilter);
  const [minConfidenceFilter, setMinConfidenceFilter] = useState<number>(0);
  const [selectedFileForAnalyzer, setSelectedFileForAnalyzer] = useState<AcousticFile | any>(null);
  const [analyzerOpen, setAnalyzerOpen] = useState(false);
  const [acousticFiles, setAcousticFiles] = useState<AcousticFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState<string | null>('Timestamp');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const fetchAudioClips = useCallback(async (showLoading = true) => {
    if (!deploymentId) return;

    if (showLoading) setLoading(true);
    try {
      const [clipResponse, fileListResponse] = await Promise.all([
        clientService.api.getDeploymentTopUniqueSpeciesClipsApiV1DeploymentsDeploymentIdBiodiversityAudioClipsGet(deploymentId),
        clientService.api
          .getFilesForDeploymentApiV1AdminFilesDeploymentIdGet(deploymentId)
          .catch(() => null),
      ]);

      const dataDict = clipResponse.data || {};
      const mappedFiles: AcousticFile[] = [];

      Object.entries(dataDict).forEach(([speciesName, clips]) => {
        (clips as any[]).forEach((clip: any) => {
          mappedFiles.push({
            fileName: clip.file_name || 'Unknown File',
            timestamp: clip.date && clip.time ? `${clip.date} ${clip.time}` : 'N/A',
            duration: clip.duration ? (typeof clip.duration === 'number' ? `${Math.floor(clip.duration / 60).toString().padStart(2, '0')}:${Math.floor(clip.duration % 60).toString().padStart(2, '0')}` : clip.duration) : '00:00',
            status: 'ANALYZED' as const,
            clipData: clip,
            detections: [{
              name: speciesName,
              scientificName: speciesName,
              confidence: clip.confidence || 100,
            }]
          });
        });
      });

      const analysedKeys = new Set(mappedFiles.map((f) => nameKey(f.fileName)));
      (fileListResponse?.data ?? []).forEach((record) => {
        if (analysedKeys.has(nameKey(record.name))) return;
        analysedKeys.add(nameKey(record.name));
        mappedFiles.push({
          fileName: record.name,
          fileId: record.file_id,
          timestamp: formatTimestamp(record.created_at),
          duration: '00:00',
          status: STATUS_BY_FILE_STATUS[record.status] ?? 'PROCESSING',
        });
      });

      setAcousticFiles(mappedFiles);
    } catch (error) {
      console.error('Failed to fetch audio clips for deployment:', error);
      setAcousticFiles([]);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [deploymentId]);

  useEffect(() => {
    fetchAudioClips();
  }, [fetchAudioClips, refreshKey]);

  const hasProcessingRow = acousticFiles.some((f) => f.status === 'PROCESSING');
  useEffect(() => {
    if (!deploymentId || !hasProcessingRow) return undefined;
    const timer = setInterval(() => {
      fetchAudioClips(false);
    }, 2000);
    return () => clearInterval(timer);
  }, [deploymentId, hasProcessingRow, fetchAudioClips]);

  const availableSpecies = useMemo(() => {
    const map = new Map<string, { name: string; scientificName: string }>();
    acousticFiles.forEach((f) => {
      (f.detections || []).forEach((d) => {
        if (d.name && !map.has(d.name)) {
          map.set(d.name, { name: d.name, scientificName: d.scientificName || '' });
        }
      });
    });
    return Array.from(map.values());
  }, [acousticFiles]);

  useEffect(() => {
    const matched = availableSpecies.find(
      (sp) =>
        sp.name.toLowerCase() === initialSpeciesFilter.toLowerCase() ||
        sp.scientificName.toLowerCase() === initialSpeciesFilter.toLowerCase()
    );
    setSpeciesFilter(matched ? matched.name : initialSpeciesFilter);
  }, [initialSpeciesFilter, availableSpecies]);

  const filteredFiles = useMemo(() => {
    return acousticFiles.filter((file) => {
      const matchesSpecies =
        !speciesFilter ||
        (file.detections || []).some(
          (d) =>
            d.name.toLowerCase().includes(speciesFilter.toLowerCase()) ||
            d.scientificName.toLowerCase().includes(speciesFilter.toLowerCase()) ||
            speciesFilter.toLowerCase().includes(d.name.toLowerCase()) ||
            speciesFilter.toLowerCase().includes(d.scientificName.toLowerCase())
        );

      const matchesConfidence =
        minConfidenceFilter === 0 ||
        (file.detections || []).some((d) => d.confidence >= minConfidenceFilter);

      return matchesSpecies && matchesConfidence;
    });
  }, [acousticFiles, speciesFilter, minConfidenceFilter]);

  const sortedFiles = useMemo(() => {
    if (!sortColumn) return filteredFiles;

    return [...filteredFiles].sort((a, b) => {
      let aVal: any = '';
      let bVal: any = '';

      if (sortColumn === 'File Name') {
        aVal = (a.fileName || '').toLowerCase();
        bVal = (b.fileName || '').toLowerCase();
      } else if (sortColumn === 'Timestamp') {
        const timeA = new Date(a.timestamp || 0).getTime();
        const timeB = new Date(b.timestamp || 0).getTime();
        if (!isNaN(timeA) && !isNaN(timeB)) {
          aVal = timeA;
          bVal = timeB;
        } else {
          aVal = (a.timestamp || '').toLowerCase();
          bVal = (b.timestamp || '').toLowerCase();
        }
      } else if (sortColumn === 'Duration') {
        const parseDuration = (dur: string) => {
          if (!dur) return 0;
          const parts = dur.split(':');
          if (parts.length === 2) {
            return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
          }
          return 0;
        };
        aVal = parseDuration(a.duration);
        bVal = parseDuration(b.duration);
      } else if (sortColumn === 'Status') {
        aVal = (a.status || '').toLowerCase();
        bVal = (b.status || '').toLowerCase();
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredFiles, sortColumn, sortOrder]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%', pb: 4 }}>
      {/* Filter Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          p: 2,
          bgcolor: 'rgba(255, 255, 255, 0.015)',
          border: '1px solid #1E1E1E',
          borderRadius: '10px',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          {/* Species Dropdown Filter */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '11px', color: '#666', fontWeight: 700, textTransform: 'uppercase' }}>
              Species:
            </Typography>
            <Select
              size="small"
              value={speciesFilter}
              onChange={(e) => setSpeciesFilter(e.target.value)}
              displayEmpty
              sx={{
                bgcolor: '#111',
                color: speciesFilter ? '#10B981' : '#AAA',
                fontWeight: 600,
                fontSize: '12px',
                height: '36px',
                borderRadius: '8px',
                border: '1px solid #282828',
                minWidth: '180px',
                '& .MuiSelect-select': { py: 0.5, px: 1.5 },
                '& .MuiSvgIcon-root': { color: '#10B981' },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: '#111',
                    border: '1px solid #222',
                    color: '#fff',
                    maxHeight: 280,
                    '& .MuiMenuItem-root': {
                      fontSize: '12px',
                      '&.Mui-selected': { bgcolor: 'rgba(16,185,129,0.15)', color: '#10B981' },
                    },
                  },
                },
              }}
            >
              <MenuItem value="">All Species</MenuItem>
              {availableSpecies.map((sp) => (
                <MenuItem key={sp.name} value={sp.name}>
                  {sp.name} <span style={{ color: '#666', fontSize: '11px', marginLeft: '6px', fontStyle: 'italic' }}>({sp.scientificName})</span>
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Confidence Score Filter */}
          {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '11px', color: '#666', fontWeight: 700, textTransform: 'uppercase' }}>
              Confidence:
            </Typography>
            <Select
              size="small"
              value={minConfidenceFilter}
              onChange={(e) => setMinConfidenceFilter(Number(e.target.value))}
              sx={{
                bgcolor: '#111',
                color: minConfidenceFilter > 0 ? '#10B981' : '#AAA',
                fontWeight: 600,
                fontSize: '12px',
                height: '36px',
                borderRadius: '8px',
                border: '1px solid #282828',
                minWidth: '160px',
                '& .MuiSelect-select': { py: 0.5, px: 1.5 },
                '& .MuiSvgIcon-root': { color: '#10B981' },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: '#111',
                    border: '1px solid #222',
                    color: '#fff',
                    '& .MuiMenuItem-root': {
                      fontSize: '12px',
                      '&.Mui-selected': { bgcolor: 'rgba(16,185,129,0.15)', color: '#10B981' },
                    },
                  },
                },
              }}
            >
              <MenuItem value={0}>All Confidence Scores</MenuItem>
              <MenuItem value={60}>&ge; 60% Confidence</MenuItem>
              <MenuItem value={75}>&ge; 75% Confidence</MenuItem>
              <MenuItem value={85}>&ge; 85% Confidence</MenuItem>
              <MenuItem value={90}>&ge; 90% Confidence</MenuItem>
            </Select>
          </Box> */}

          {/* Site and Deployment Chips */}
          {siteName && deploymentName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
              <Chip
                label={siteName}
                sx={{
                  bgcolor: '#111',
                  color: '#10B981',
                  fontWeight: 600,
                  fontSize: '12px',
                  border: '1px solid #10B981',
                  borderRadius: '8px',
                  height: '36px',
                }}
              />
              <ArrowRightIcon sx={{ color: '#10B981', fontSize: 18 }} />
              {deploymentsList && deploymentsList.length > 0 && onDeploymentSelect ? (
                <Select
                  value={deploymentId || ''}
                  onChange={(e) => {
                    const selected = deploymentsList.find((d) => d.deployment_id === e.target.value);
                    if (selected && onDeploymentSelect) {
                      onDeploymentSelect(selected.deployment_id, selected.deployment_name, selected.site_name);
                    }
                  }}
                  size="small"
                  sx={{
                    bgcolor: '#111',
                    color: '#10B981',
                    fontWeight: 600,
                    fontSize: '12px',
                    border: '1px dashed #10B981',
                    borderRadius: '8px',
                    height: '36px',
                    '& .MuiSelect-select': { py: 0, px: 2, display: 'flex', alignItems: 'center' },
                    '& .MuiSvgIcon-root': { color: '#10B981' },
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#1a1a1a',
                        color: '#eee',
                        border: '1px solid #333',
                        mt: 0.5,
                        borderRadius: '8px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                        maxHeight: '250px',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': { width: '4px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': { background: '#444', borderRadius: '4px' },
                        '&::-webkit-scrollbar-thumb:hover': { background: '#555' },
                        '& .MuiMenuItem-root': {
                          fontSize: '12px',
                          py: 1,
                          px: 2,
                          transition: 'background-color 0.2s, color 0.2s',
                        },
                        '& .MuiMenuItem-root:hover': { bgcolor: '#2a2a2a', color: '#10B981' },
                        '& .Mui-selected': { bgcolor: '#2a2a2a !important', color: '#10B981', fontWeight: 600 },
                      },
                    },
                  }}
                >
                  {deploymentsList.map((d) => (
                    <MenuItem key={d.deployment_id} value={d.deployment_id}>
                      {d.deployment_name}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Chip
                  label={deploymentName}
                  sx={{
                    bgcolor: '#111',
                    color: '#10B981',
                    fontWeight: 600,
                    fontSize: '12px',
                    border: '1px dashed #10B981',
                    borderRadius: '8px',
                    height: '36px',
                  }}
                />
              )}
            </Box>
          )}
        </Box>

        {/* Filter Stats Badge, Reset & Hierarchy Chips */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontSize: '11px', color: '#888', fontWeight: 600 }}>
            Showing <b>{filteredFiles.length}</b> of {acousticFiles.length} audio files
          </Typography>
          {(Boolean(speciesFilter) || minConfidenceFilter > 0) && (
            <Button
              size="small"
              onClick={() => {
                setSpeciesFilter('');
                setMinConfidenceFilter(0);
              }}
              sx={{
                fontSize: '10px',
                fontWeight: 700,
                color: '#EF4444',
                bgcolor: 'rgba(239, 68, 68, 0.08)',
                border: '1px solid rgba(239, 68, 68, 0.25)',
                borderRadius: '6px',
                px: 1,
                py: 0.25,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'rgba(239, 68, 68, 0.18)',
                },
              }}
            >
              Clear Filters
            </Button>
          )}
          <MarkCompleteButton deploymentId={deploymentId} />
        </Box>
      </Box>

      {/* Acoustic Files Table */}
      <Box sx={{ border: '1px solid rgba(255, 255, 255, 0.05)', borderRadius: '12px', overflow: 'hidden', background: 'rgba(255, 255, 255, 0.005)' }}>
        <Box sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { height: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#333', borderRadius: '4px' } }}>
          <Box sx={{ minWidth: '700px' }}>
            {/* Table Headers */}
            <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '3fr 2fr 1fr 1fr 1fr',
            gap: 2,
            p: 2,
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            bgcolor: 'rgba(255, 255, 255, 0.01)',
          }}
        >
          {['File Name', 'Timestamp', 'Duration', 'Status', 'Action'].map((header) => {
            const isSortable = ['File Name', 'Timestamp', 'Duration', 'Status'].includes(header);
            const isActive = sortColumn === header;
            return (
              <Typography
                key={header}
                sx={{
                  color: 'rgba(255, 255, 255, 0.35)',
                  fontSize: '9px',
                  fontWeight: 700,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {header}
                {isSortable && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (isActive) {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortColumn(header);
                        setSortOrder('asc');
                      }
                    }}
                    style={{
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '4px',
                      marginLeft: '2px',
                      color: isActive ? '#10b981' : 'rgba(255, 255, 255, 0.25)',
                      fontSize: '12px',
                    }}
                    title={`Sort by ${header}`}
                  >
                    {isActive ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}
                  </span>
                )}
              </Typography>
            );
          })}
        </Box>

        {/* Table Rows */}
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '13px', fontStyle: 'italic' }}>
              Loading audio clips...
            </Typography>
          </Box>
        ) : !deploymentId ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '13px', fontStyle: 'italic' }}>
              Select a Deployment from Map
            </Typography>
          </Box>
        ) : filteredFiles.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '13px', fontStyle: 'italic' }}>
              No audio files match the selected species or confidence filters.
            </Typography>
          </Box>
        ) : (
          sortedFiles.map((file, idx) => {
            const isFailed = file.status === 'FAILED';
            const isProcessing = file.status === 'PROCESSING';

            let statusColor = '#10b981';
            let statusBg = 'rgba(16, 185, 129, 0.08)';
            let statusBorder = 'rgba(16, 185, 129, 0.2)';
            if (isFailed) {
              statusColor = '#ef4444';
              statusBg = 'rgba(239, 68, 68, 0.08)';
              statusBorder = 'rgba(239, 68, 68, 0.2)';
            } else if (isProcessing) {
              statusColor = '#06b6d4';
              statusBg = 'rgba(6, 182, 212, 0.08)';
              statusBorder = 'rgba(6, 182, 212, 0.2)';
            }

            return (
              <Box
                key={file.fileName + '-' + idx}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '3fr 2fr 1fr 1fr 1fr',
                  gap: 2,
                  p: 2,
                  alignItems: 'center',
                  borderBottom: idx < filteredFiles.length - 1 ? '1px solid rgba(255, 255, 255, 0.03)' : 'none',
                  transition: 'background 0.2s',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.015)',
                  },
                }}
              >
                {/* File Name Cell */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {isFailed ? (
                    <WarningIcon sx={{ color: '#ef4444', fontSize: 18 }} />
                  ) : (
                    <FileIcon sx={{ color: '#10b981', fontSize: 18 }} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      title={file.fileName}
                      sx={{
                        color: isFailed ? '#f87171' : '#fff',
                        fontSize: '13px',
                        fontWeight: 600,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-all',
                      }}
                    >
                      {file.fileName}
                    </Typography>
                  </Box>
                </Box>

                {/* Timestamp Cell */}
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '13px', fontWeight: 500 }}>
                  {file.timestamp}
                </Typography>

                {/* Duration Cell */}
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '13px', fontWeight: 500 }}>
                  {file.duration}
                </Typography>

                {/* Status Badge Cell */}
                <Box>
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '4px',
                      border: `1.5px solid ${statusBorder}`,
                      bgcolor: statusBg,
                      color: statusColor,
                      fontSize: '9px',
                      fontWeight: 700,
                      letterSpacing: '0.5px',
                      textAlign: 'center',
                    }}
                  >
                    {file.status}
                  </Box>
                </Box>

                {/* Action Icons Cell */}
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {isFailed ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '50%', border: '1px solid rgba(255, 255, 255, 0.1)', cursor: 'pointer', color: 'rgba(255, 255, 255, 0.5)', '&:hover': { color: '#fff', borderColor: 'rgba(255,255,255,0.3)' } }}>
                        <RetryIcon sx={{ fontSize: 16 }} />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '50%', border: '1px solid rgba(255, 255, 255, 0.1)', cursor: 'pointer', color: 'rgba(255, 255, 255, 0.5)', '&:hover': { color: '#fff', borderColor: 'rgba(255,255,255,0.3)' } }}>
                        <InfoIcon sx={{ fontSize: 16 }} />
                      </Box>
                    </>
                  ) : (
                    <>
                      <AudioRowPlayer file={file} />
                      <Box
                        onClick={() => {
                          setSelectedFileForAnalyzer(file);
                          setAnalyzerOpen(true);
                        }}
                        title="Analyze audio in Spectrogram & Waveform viewer"
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '50%', border: '1px solid rgba(255, 255, 255, 0.1)', cursor: 'pointer', color: '#10B981', '&:hover': { color: '#fff', borderColor: '#10B981', bgcolor: 'rgba(16,185,129,0.1)' } }}
                      >
                        <SpectrogramIcon sx={{ fontSize: 16 }} />
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            );
          })
        )}
          </Box>
        </Box>
      </Box>

      {/* Audio Analyzer Popup Modal */}
      <AudioAnalyzerPopup
        open={analyzerOpen}
        onClose={() => setAnalyzerOpen(false)}
        file={selectedFileForAnalyzer}
        deploymentId={deploymentId}
      />
    </Box>
  );
};

export default AcousticDataView;
